import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { shopApi } from "@/lib/api";

function useCountdown(endAt) {
  const [remaining, setRemaining] = useState(() => Math.max(0, new Date(endAt).getTime() - Date.now()));
  useEffect(() => {
    if (!endAt) return;
    const id = setInterval(() => {
      setRemaining(Math.max(0, new Date(endAt).getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(id);
  }, [endAt]);
  const s = Math.floor(remaining / 1000);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
    expired: remaining <= 0,
  };
}

const Cell = ({ n, label }) => (
  <span className="inline-flex flex-col items-center leading-none">
    <span className="tabular-nums font-bold text-primary text-sm sm:text-base">{String(n).padStart(2, "0")}</span>
    <span className="text-[9px] uppercase tracking-widest text-primary/60">{label}</span>
  </span>
);

export default function CountdownBanner() {
  const { data } = useQuery({
    queryKey: ["countdown"],
    queryFn: shopApi.countdown,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const t = useCountdown(data?.end_at || "");
  const [hidden, setHidden] = useState(false);

  if (!data?.active || !data.end_at) return null;
  if (t.expired) return null;
  if (hidden) return null;

  return (
    <div className="relative bg-black border-b border-primary/40" data-testid="countdown-banner">
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-2 text-primary text-xs sm:text-sm font-medium">
          <Flame className="h-4 w-4" fill="currentColor" />
          {data.message || "Launch sale ends in"}
        </span>
        <span className="inline-flex items-center gap-2">
          <Cell n={t.d} label="d" />
          <span className="text-primary/40">:</span>
          <Cell n={t.h} label="h" />
          <span className="text-primary/40">:</span>
          <Cell n={t.m} label="m" />
          <span className="text-primary/40">:</span>
          <Cell n={t.s} label="s" />
        </span>
        {data.cta_label && data.cta_url && (
          <a href={data.cta_url} className="text-xs sm:text-sm underline text-primary hover:text-white transition ml-1" data-testid="countdown-cta">
            {data.cta_label} →
          </a>
        )}
      </div>
      <button
        onClick={() => setHidden(true)}
        aria-label="Dismiss countdown"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-primary/50 hover:text-primary text-lg leading-none px-2"
        data-testid="countdown-dismiss"
      >
        ×
      </button>
    </div>
  );
}
